import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { api, formatValue } from "@/lib/api";
import { useFormat } from "@/contexts/FormatContext";
import { Plus, X, Loader2, Bot, Share2 } from "lucide-react";
import TradeVerdictCard from "@/components/TradeVerdictCard";
import DmeTag from "@/components/DmeTag";
import PlayerBadge from "@/components/PlayerBadge";
import PlayerTag from "@/components/PlayerTag";
import { DivergenceChip } from "@/components/MarketVectors";

// Elite players carry a real-world trade premium (X-Factor +20% / Gold +15% / Silver +10%).
const taxedVal = (p) => Math.round(p.value * (1 + (p.badge?.tax || 0)));

function PlayerPicker({ side, players, onAdd }) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    if (!q) return players.slice(0, 12);
    const ql = q.toLowerCase();
    return players.filter(p => p.name.toLowerCase().includes(ql)).slice(0, 12);
  }, [q, players]);
  return (
    <div className="relative">
      <input
        className="input-dark"
        placeholder={`Add to ${side === 'a' ? 'Side A' : 'Side B'}…`}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        data-testid={`picker-${side}`}
      />
      {q && (
        <div className="absolute z-10 left-0 right-0 mt-1 bg-[var(--elev)] border border-[var(--border)] rounded-md max-h-64 overflow-y-auto">
          {filtered.map(p => (
            <button key={p.player_id} onClick={() => { onAdd(p); setQ(""); }} className="flex justify-between items-center w-full p-2 text-sm hover:bg-white/5" data-testid={`pick-${side}-${p.player_id}`}>
              <PlayerTag player={p} size={26} link={false} showPos />
              <span className="tabular text-[var(--accent)] shrink-0">{formatValue(p.value)}</span>
            </button>
          ))}
          {filtered.length === 0 && <div className="p-3 text-sm text-[var(--text-3)]">No matches</div>}
        </div>
      )}
    </div>
  );
}

function Side({ label, items, onAdd, onRemove, players, total, sideKey, teams, selectedTeam, onTeamChange, managerName }) {
  const roster = teams && selectedTeam ? players : null;
  return (
    <div className="card-bordered p-5 flex-1 min-w-0">
      <div className="flex items-start justify-between mb-3 gap-3">
        <div className="min-w-0 flex-1">
          <div className="font-display font-semibold">{label}{managerName ? ` · ${managerName}` : ""}</div>
          {teams && teams.length > 0 && (
            <select value={selectedTeam} onChange={(e) => onTeamChange(e.target.value)}
              className="input-dark w-full mt-1.5 text-xs" data-testid={`team-select-${sideKey}`}>
              <option value="">— Whole market —</option>
              {teams.map(t => <option key={t.roster_id} value={t.roster_id}>{t.manager}</option>)}
            </select>
          )}
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <DmeTag />
          <div className="tabular text-[var(--accent)] text-xl" data-testid={`total-${sideKey}`}>{formatValue(total)}</div>
        </div>
      </div>
      <PlayerPicker side={sideKey} players={players} onAdd={onAdd} />
      {roster && (
        <div className="mt-3">
          <div className="text-[10px] tracking-widest text-[var(--text-3)] mb-1.5">ROSTER — TAP TO ADD</div>
          <div className="max-h-48 overflow-y-auto space-y-1 pr-1" data-testid={`roster-list-${sideKey}`}>
            {roster.filter(p => !items.some(it => it.player_id === p.player_id)).map(p => (
              <button key={p.player_id} onClick={() => onAdd(p)}
                className="flex justify-between items-center w-full p-2 text-xs hover:bg-white/5 rounded border border-[var(--border)]"
                data-testid={`roster-add-${sideKey}-${p.player_id}`}>
                <PlayerTag player={p} size={24} link={false} showPos />
                <span className="tabular text-[var(--accent)] shrink-0">{formatValue(p.value)}</span>
              </button>
            ))}
            {roster.filter(p => !items.some(it => it.player_id === p.player_id)).length === 0 && (
              <div className="text-xs text-[var(--text-3)] py-2 text-center">All rostered players added</div>
            )}
          </div>
        </div>
      )}
      <div className="mt-3 space-y-1">
        {items.map(p => (
          <div key={p.player_id} className="flex justify-between items-center text-sm bg-black/40 border border-[var(--border)] rounded-md p-2" data-testid={`item-${sideKey}-${p.player_id}`}>
            <div className="min-w-0">
              <PlayerTag player={p} size={28} showPos data-testid={`link-${sideKey}-${p.player_id}`} />
              {p.badge ? <div className="text-[10px] tracking-widest text-[var(--text-3)] mt-0.5 ml-9">+{Math.round(p.badge.tax * 100)}% tax</div> : null}
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <DivergenceChip divergence={p.divergence} />
              <span className="tabular" title={p.badge ? `Base ${formatValue(p.value)} +${Math.round(p.badge.tax * 100)}% X-Factor tax` : undefined}>{formatValue(taxedVal(p))}</span>
              <button onClick={() => onRemove(p.player_id)} className="text-[var(--text-3)] hover:text-red-400"><X className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="text-xs text-[var(--text-3)] py-3 text-center">Empty</div>}
      </div>
    </div>
  );
}

export default function TradeCalculator() {
  const navigate = useNavigate();
  const location = useLocation();
  const { format } = useFormat();
  const [players, setPlayers] = useState([]);
  const [a, setA] = useState([]);
  const [b, setB] = useState([]);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showCard, setShowCard] = useState(false);
  const [leagues, setLeagues] = useState([]);
  const [leagueId, setLeagueId] = useState("");
  const [teams, setTeams] = useState([]);
  const [teamA, setTeamA] = useState("");
  const [teamB, setTeamB] = useState("");

  useEffect(() => { api.get("/leagues").then(r => setLeagues(r.data.leagues || [])); }, []);

  useEffect(() => {
    if (!leagueId) { setTeams([]); setTeamA(""); setTeamB(""); return; }
    api.get(`/leagues/${leagueId}/teams`).then(r => setTeams(r.data.teams || [])).catch(() => setTeams([]));
  }, [leagueId, format]);

  useEffect(() => {
    api.get("/players?limit=600").then(r => {
      const list = r.data.players || [];
      setPlayers(list);
      // Re-map current selections to refreshed values on format change.
      const byId = Object.fromEntries(list.map(p => [p.player_id, p]));
      setA(prev => prev.map(x => byId[x.player_id] || x));
      setB(prev => prev.map(x => byId[x.player_id] || x));
      setResult(null);
    });
  }, [format]);

  // Preload a trade when arriving from the AI Advisor's "Open in Calculator"
  useEffect(() => {
    const st = location.state;
    if (players.length && (st?.preloadA?.length || st?.preloadB?.length)) {
      const byId = Object.fromEntries(players.map(p => [p.player_id, p]));
      setA((st.preloadA || []).map(id => byId[id]).filter(Boolean));
      setB((st.preloadB || []).map(id => byId[id]).filter(Boolean));
    }
  }, [players, location.state]);

  const totalA = a.reduce((s, x) => s + taxedVal(x), 0);
  const totalB = b.reduce((s, x) => s + taxedVal(x), 0);

  const teamObjA = teams.find(t => String(t.roster_id) === String(teamA));
  const teamObjB = teams.find(t => String(t.roster_id) === String(teamB));
  const poolA = teamObjA ? teamObjA.players : players;
  const poolB = teamObjB ? teamObjB.players : players;

  const askAi = () => {
    if (a.length === 0 || b.length === 0) return;
    const label = (x) => x.is_pick && x.origin ? `${x.name} (originally ${x.origin}'s)` : `${x.name} (${x.position})`;
    const sideA = a.map(label).join(", ");
    const sideB = b.map(label).join(", ");
    const tradeContext = `Trade under consideration — Side A gives: ${sideA}. Side B gives: ${sideB}.`;
    navigate("/app/advisor", { state: {
      tradeContext,
      prefill: `Should I make this trade? I give ${sideA} and receive ${sideB}.`,
      aIds: a.map(x => x.asset_id || x.player_id),
      bIds: b.map(x => x.asset_id || x.player_id),
    } });
  };

  const evaluate = async () => {
    if (a.length === 0 || b.length === 0) return;
    setBusy(true);
    try {
      const r = await api.post("/trade/evaluate", { side_a: a.map(x => x.asset_id || x.player_id), side_b: b.map(x => x.asset_id || x.player_id), league_id: leagueId || null });
      setResult(r.data);
    } finally { setBusy(false); }
  };

  const winnerLabel = result?.winner === "side_a" ? "Side A wins" : result?.winner === "side_b" ? "Side B wins" : "Even trade";

  return (
    <div className="space-y-6 fade-up">
      <header>
        <div className="text-[var(--accent)] text-xs tracking-widest mb-1">// CALCULATOR</div>
        <h1 className="font-display text-4xl font-bold" data-testid="tc-heading">Dynasty Trade Calculator</h1>
        <p className="text-[var(--text-2)] mt-2 max-w-xl">Search and add players + picks to each side. Powered by the Dynasty Metrics Value Engine — proprietary asset values, continuously re-priced and market-verified.</p>
      </header>

      <div className="flex flex-col md:flex-row gap-4">
        <Side label="Side A gives" items={a} onAdd={(p) => setA([...a, p])} onRemove={(id) => setA(a.filter(x => x.player_id !== id))} players={poolA} total={totalA} sideKey="a"
          teams={teams} selectedTeam={teamA} managerName={teamObjA?.manager}
          onTeamChange={(v) => { setTeamA(v); setA([]); setResult(null); }} />
        <Side label="Side B gives" items={b} onAdd={(p) => setB([...b, p])} onRemove={(id) => setB(b.filter(x => x.player_id !== id))} players={poolB} total={totalB} sideKey="b"
          teams={teams} selectedTeam={teamB} managerName={teamObjB?.manager}
          onTeamChange={(v) => { setTeamB(v); setB([]); setResult(null); }} />
      </div>

      <div className="flex flex-wrap items-center gap-4">
        {leagues.length > 0 && (
          <select value={leagueId} onChange={(e) => setLeagueId(e.target.value)} className="input-dark w-auto text-sm" data-testid="trade-league-select">
            <option value="">General (no league)</option>
            {leagues.map(l => <option key={l.league_id} value={l.league_id}>{l.name}</option>)}
          </select>
        )}
        <button onClick={evaluate} disabled={busy || a.length === 0 || b.length === 0} className="btn-primary inline-flex items-center gap-2" data-testid="trade-evaluate-btn">
          {busy ? <Loader2 className="w-4 h-4 spin" /> : <Plus className="w-4 h-4" />}
          Evaluate trade
        </button>
        <button onClick={() => { setA([]); setB([]); setResult(null); }} className="btn-secondary text-sm" data-testid="trade-clear">Clear</button>
        <button onClick={askAi} disabled={a.length === 0 || b.length === 0} className="btn-secondary text-sm inline-flex items-center gap-2 ml-auto" data-testid="trade-ask-ai">
          <Bot className="w-4 h-4" /> Ask AI Advisor
        </button>
      </div>

      {result && (
        <div className="card-bordered p-6 fade-up" data-testid="trade-result">
          <div className="flex flex-wrap justify-between items-center gap-4">
            <div>
              <div className="text-xs tracking-widest text-[var(--text-3)]">VERDICT</div>
              <div className="font-display text-3xl font-bold mt-1" data-testid="trade-winner">{winnerLabel}</div>
              <div className="text-sm text-[var(--text-2)] mt-1 max-w-md" data-testid="trade-reason">{result.reason || `Value differential: ${result.diff >= 0 ? "+" : ""}${formatValue(result.diff)}`}</div>
            </div>
            <div className="text-right">
              <div className="text-xs tracking-widest text-[var(--text-3)]">FAIRNESS</div>
              <div className="font-mono text-5xl font-semibold mt-1" data-testid="trade-fairness">
                <span className={result.fairness >= 90 ? "text-emerald-400" : result.fairness >= 75 ? "text-amber-400" : "text-red-400"}>{result.fairness}%</span>
              </div>
            </div>
          </div>
          <div className="mt-5 pt-4 border-t border-[var(--border)]">
            <button onClick={() => setShowCard(true)} className="btn-primary text-sm inline-flex items-center gap-2" data-testid="share-verdict-btn">
              <Share2 className="w-4 h-4" /> Share Verdict Card
            </button>
            <span className="text-xs text-[var(--text-3)] ml-3">Export a branded image for your league chat.</span>
          </div>
        </div>
      )}

      {showCard && (
        <TradeVerdictCard result={result} format={format} onClose={() => setShowCard(false)} />
      )}
    </div>
  );
}
